'use client';

import { getBackgroundColor, getTextColor } from 'theme-utils';
import type {
  ThemeColorsBackground,
  ThemeColorsBorder,
  ThemeColorsText,
  ThemeRadii,
  ThemeSpaces,
} from '@frameio/vapor-theme';
import { theme } from '@frameio/vapor-theme';

type Display =
  | 'block'
  | 'inline'
  | 'none'
  | 'inline-block'
  | 'flex'
  | 'inline-flex'
  | 'grid'
  | 'inline-grid';

type TextColor = ThemeColorsText;

type BackgroundColor = ThemeColorsBackground;

type AlignItems = 'flex-start' | 'center' | 'flex-end' | 'baseline';

type JustifyContent =
  | 'flex-start'
  | 'center'
  | 'flex-end'
  | 'space-between'
  | 'space-around';

type FlexDirection = 'row' | 'row-reverse' | 'column' | 'column-reverse';

type FlexWrap = 'wrap' | 'nowrap';

type FlexGrow = '0' | '1';

type FlexShrink = '0' | '1';

type FlexBasis = string;

type TextAlign = 'left' | 'center' | 'right';

type Height = '100%' | ThemeSpaces | string;

type Width = '100%' | ThemeSpaces | string;

type Position = 'absolute' | 'relative' | 'fixed';

type Overflow = 'hidden' | 'scroll' | 'visible' | 'auto';

type BorderBottom = boolean;

type BorderColor = ThemeColorsBorder;

type Sizes = {
  width?: Width;
  height?: Height;
  minWidth?: Width;
  minHeight?: Height;
  maxWidth?: Width;
  maxHeight?: Height;
};

type Positions = {
  top?: string;
  left?: string;
  right?: string;
  bottom?: string;
};

type Paddings = {
  padding?: ThemeSpaces;
  paddingX?: ThemeSpaces;
  paddingY?: ThemeSpaces;
  paddingTop?: ThemeSpaces;
  paddingLeft?: ThemeSpaces;
  paddingRight?: ThemeSpaces;
  paddingBottom?: ThemeSpaces;
};

type Gap = ThemeSpaces;

export type BoxStyleOptions = {
  display?: Display;
  alignItems?: AlignItems;
  justifyContent?: JustifyContent;
  flexDirection?: FlexDirection;
  flexWrap?: FlexWrap;
  flexGrow?: FlexGrow;
  flexShrink?: FlexShrink;
  flexBasis?: FlexBasis;
  textAlign?: TextAlign;
  height?: Height;
  width?: Width;
  position?: Position;
  overflow?: Overflow;
  color?: TextColor;
  backgroundColor?: BackgroundColor;
  borderRadius?: ThemeRadii;
  transform?: string;
  /* borderBottom prop uses a box shadow to draw the border. */
  borderBottom?: BorderBottom;
  borderColor?: BorderColor;
  gap?: Gap;
} & Paddings &
  Positions &
  Sizes;

export const boxStyles = (props: BoxStyleOptions) => {
  const styles = (
    [
      'display',
      'position',
      'top',
      'right',
      'bottom',
      'left',
      'flexDirection',
      'flexShrink',
      'flexGrow',
      'flexWrap',
      'alignItems',
      'flexBasis',
      'justifyContent',
      'textAlign',
      'overflow',
      'transform',
    ] satisfies Array<keyof BoxStyleOptions>
  ).reduce((acc, key) => {
    // pick out the desired styles that are defined in the props
    if (Object.hasOwn(props, key)) acc[key] = props[key];
    return acc;
  }, {});

  return {
    ...styles,
    color: getTextColor(props.color),
    backgroundColor: getBackgroundColor(props.backgroundColor),
    borderRadius: theme.radii[props.borderRadius],
    boxShadow:
      props.borderBottom && getBorder({ borderColor: props.borderColor }),
    ...getSizes(props),
    ...getPadding(props),
    gap: theme.spaces[props.gap] ?? props.gap,
  };
};

const getBorder = ({ borderColor }) => {
  return `inset 0 -1px 0 0 ${borderColor || theme.colors.border.primary}`;
};

const getPadding = ({
  padding,
  paddingTop,
  paddingY,
  paddingBottom,
  paddingX,
  paddingLeft,
  paddingRight,
}: Paddings) => {
  const resolvedPaddingTop: ThemeSpaces = paddingTop || paddingY || padding;
  const resolvedPaddingBottom = paddingBottom || paddingY || padding;
  const resolvedPaddingLeft = paddingLeft || paddingX || padding;
  const resolvedPaddingRight = paddingRight || paddingX || padding;

  return {
    paddingLeft: theme.spaces[resolvedPaddingLeft],
    paddingTop: theme.spaces[resolvedPaddingTop],
    paddingBottom: theme.spaces[resolvedPaddingBottom],
    paddingRight: theme.spaces[resolvedPaddingRight],
  };
};

const getSizes = ({
  width,
  height,
  maxWidth,
  maxHeight,
  minWidth,
  minHeight,
}: Sizes) => {
  return {
    width: theme.spaces[width] ?? width,
    height: theme.spaces[height] ?? height,
    maxWidth: theme.spaces[maxWidth] ?? maxWidth,
    maxHeight: theme.spaces[maxHeight] ?? maxHeight,
    minWidth: theme.spaces[minWidth] ?? minWidth,
    minHeight: theme.spaces[minHeight] ?? minHeight,
  };
};
